import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Lang } from '../types';
interface FooterProps {
  lang: Lang;
}
type ModalType = 'privacy' | 'terms' | null;
export function Footer({
  lang
}: FooterProps) {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  // Handle escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveModal(null);
    };
    if (activeModal) {
      window.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [activeModal]);
  return <>
      <footer className="bg-studio-bgAlt border-t border-studio-border py-8" data-id="element-129">
        <div className="max-w-7xl mx-auto px-6" data-id="element-130">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-131">
            <div data-id="element-132">
              <h4 className="text-studio-accent font-playfair font-semibold mb-4" data-id="element-133">
                Artist
              </h4>
              <p className="text-studio-muted" data-id="element-134">Julián Ospina — Barboxa Ink</p>
            </div>
            <div data-id="element-135">
              <h4 className="text-studio-accent font-playfair font-semibold mb-4" data-id="element-136">
                Studio
              </h4>
              <p className="text-studio-muted" data-id="element-137">Piajas Ink Tattoo Studio</p>
              <p className="text-studio-muted mt-1" data-id="element-138">
                2116 SW 8th St, Miami FL 33135
              </p>
            </div>
            <div data-id="element-139">
              <h4 className="text-studio-accent font-playfair font-semibold mb-4" data-id="element-140">
                Details
              </h4>
              <p className="text-studio-muted" data-id="element-141">Specialty: Fine Line Tattoo</p>
              <p className="text-studio-muted mt-1" data-id="element-142">
                Booking: Appointment Only
              </p>
              <p className="text-studio-muted mt-1" data-id="element-143">
                Languages: English 🇺🇸 / Spanish 🇪🇸
              </p>
            </div>
            <div data-id="element-144">
              <h4 className="text-studio-accent font-playfair font-semibold mb-4" data-id="element-145">
                Social
              </h4>
              <a href="#" className="block text-studio-muted hover:text-studio-text transition-colors" data-id="element-146">
                @barboxa_ink
              </a>
              <a href="#" className="block text-studio-muted hover:text-studio-text transition-colors mt-1" data-id="element-147">
                @CapitanBarboxa
              </a>
            </div>
          </div>

          <div className="pt-8 border-t border-studio-border flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-studio-muted/50" data-id="element-148">
            <p data-id="element-149">
              © {new Date().getFullYear()} Barboxa Ink. All rights reserved.
            </p>
            <div className="flex items-center gap-3 sm:gap-4" data-id="element-150">
              <button onClick={() => setActiveModal('privacy')} className="hover:text-studio-accent transition-colors" data-id="element-151">
                {lang === 'en' ? 'Privacy Policy' : 'Política de Privacidad'}
              </button>
              <span data-id="element-152">·</span>
              <button onClick={() => setActiveModal('terms')} className="hover:text-studio-accent transition-colors" data-id="element-153">
                {lang === 'en' ? 'Terms of Service' : 'Términos de Servicio'}
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Legal Modals */}
      <AnimatePresence data-id="element-154">
        {activeModal && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-155">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setActiveModal(null)} className="absolute inset-0 bg-black/80 backdrop-blur-sm" aria-hidden="true" data-id="element-156" />
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.95
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="relative w-full max-w-2xl bg-studio-bg border border-studio-border p-6 md:p-8 max-h-[85vh] overflow-y-auto shadow-2xl rounded-sm" role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-157">
              <button onClick={() => setActiveModal(null)} className="absolute top-4 right-4 p-2 text-studio-muted hover:text-studio-accent transition-colors bg-studio-bgAlt rounded-full" aria-label="Close modal" data-id="element-158">
                <X size={20} data-id="element-159" />
              </button>

              <h2 id="modal-title" className="text-2xl md:text-3xl font-playfair font-bold text-studio-text mb-6 pr-8" data-id="element-160">
                {activeModal === 'privacy' ? lang === 'en' ? 'Privacy Policy' : 'Política de Privacidad' : lang === 'en' ? 'Terms of Service' : 'Términos de Servicio'}
              </h2>

              <div className="space-y-4 text-studio-muted text-sm md:text-base leading-relaxed" data-id="element-161">
                <p className="font-medium text-studio-text" data-id="element-162">
                  {lang === 'en' ? 'Last updated:' : 'Última actualización:'}{' '}
                  {new Date().toLocaleDateString()}
                </p>

                <p data-id="element-163">
                  {lang === 'en' ? `This is a placeholder for the ${activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}. Replace this content with your actual legal text.` : `Este es un texto de marcador de posición para ${activeModal === 'privacy' ? 'la Política de Privacidad' : 'los Términos de Servicio'}. Reemplace este contenido con su texto legal real.`}
                </p>

                <p data-id="element-164">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed
                  do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                  Ut enim ad minim veniam, quis nostrud exercitation ullamco
                  laboris nisi ut aliquip ex ea commodo consequat.
                </p>

                <h3 className="text-lg font-playfair font-semibold text-studio-text mt-6 mb-2" data-id="element-165">
                  1.{' '}
                  {lang === 'en' ? 'Information Collection' : 'Recopilación de Información'}
                </h3>
                <p data-id="element-166">
                  Duis aute irure dolor in reprehenderit in voluptate velit esse
                  cillum dolore eu fugiat nulla pariatur. Excepteur sint
                  occaecat cupidatat non proident, sunt in culpa qui officia
                  deserunt mollit anim id est laborum.
                </p>

                <h3 className="text-lg font-playfair font-semibold text-studio-text mt-6 mb-2" data-id="element-167">
                  2.{' '}
                  {lang === 'en' ? 'Use of Information' : 'Uso de la Información'}
                </h3>
                <p data-id="element-168">
                  Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut
                  odit aut fugit, sed quia consequuntur magni dolores eos qui
                  ratione voluptatem sequi nesciunt. Neque porro quisquam est,
                  qui dolorem ipsum quia dolor sit amet.
                </p>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </>;
}